import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../../content/site";
import { useSettings, useSettingsLoaded } from "../../hooks/useSettings";
import { track } from "../../lib/analytics";
import { ctaState } from "../../lib/cta";
import { useGetApp } from "../ui/GetAppModal";

export function Nav() {
  const s = useSettings();
  const settingsLoaded = useSettingsLoaded();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [barOpen, setBarOpen] = useState(true);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Wait for the saved settings so the bar never flashes the fallback text
  // before the admin's announcement loads.
  const showBar = settingsLoaded && s.announcement_enabled && barOpen;
  const { live } = ctaState(s);
  const { open } = useGetApp();

  const goPrimary = () => {
    track("nav_cta_click", {});
    if (live && s.app_store_url) {
      window.location.href = s.app_store_url;
    } else {
      open({ source: "nav" });
    }
    setMenuOpen(false);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Announcement bar — a sunny ribbon */}
      <AnimatePresence>
        {showBar && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b-2 border-navy bg-sun"
          >
            <div className="container-x flex items-center justify-center gap-3 py-2 text-center text-sm font-bold text-navy">
              <Sparkles className="hidden h-4 w-4 shrink-0 sm:block" />
              <span className="line-clamp-2 sm:truncate">{s.announcement_text}</span>
              <button
                onClick={() => setBarOpen(false)}
                aria-label="Dismiss announcement"
                className="ml-1 rounded-full p-0.5 text-navy/70 hover:bg-navy/10 hover:text-navy"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main bar */}
      <nav
        className={`transition-all duration-300 ${
          scrolled
            ? "border-b-[3px] border-navy bg-cream/90 backdrop-blur-xl"
            : "border-b-[3px] border-transparent bg-transparent"
        }`}
      >
        <div className="container-x flex h-16 items-center justify-between sm:h-[4.25rem]">
          <a href="#top" aria-label="FitBuddy home" className="shrink-0">
            <Logo />
          </a>

          <div className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="font-display text-sm font-bold text-navy/80 transition-colors hover:text-navy"
              >
                {l.label}
              </a>
            ))}
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <button onClick={goPrimary} className="btn-sun px-5 py-2 text-sm">
              {live ? "Get the app" : "Join early list"}
            </button>
          </div>

          <button
            className="grid h-10 w-10 place-items-center rounded-xl border-2 border-navy bg-white text-navy shadow-pop-xs lg:hidden"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden"
          >
            <div className="card-pop mx-3 mt-2 rounded-3xl bg-cream p-5">
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setMenuOpen(false)}
                    className="rounded-xl px-4 py-3 font-display text-base font-bold text-navy/70 hover:bg-sun-pale hover:text-navy"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
              <button
                onClick={goPrimary}
                className="btn-sun mt-4 w-full py-3 text-base"
              >
                {live ? "Get the app" : "Join the early list"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
